/**
 * Debug overlay
 * Displays mission info, trial info, keyboard inputs, and other debugging information
 */

import { KEY_CODE_TO_KEY_CONVERSION } from "../../gearshiftGame/constants";

const shouldShowCorrectLane = process.env.NODE_ENV !== "production";

/**
 * Creates and manages a debug overlay in the top-right corner
 */
export class DebugOverlay {
  constructor() {
    this.overlayLeft = null;
    this.overlayRight = null;
    this.versionBadge = null;
    this.keyHistory = []; // Stores last 5 key presses
    this.maxKeyHistory = 5;
    this.hotkeyRegistered = false;

    // Current state placeholders
    this.state = {
      // Login Information
      studyID: "-",
      participantID: "-",
      sessionID: "-",

      // Mission Configuration
      mission: "-",
      buildDateTime: "-",
      keyboardLayout: "-",
      instructionsMaxRetries: "-",
      missionLives: "-",
      missionMaxCrash: "-",
      missionMinScore: "-",
      missionNumTrials: "-",
      thisFile: "-",
      metadataSource: "-",
      rlExperiment: "-",

      // Trial Information
      trialNumber: "-",
      trialType: "-",
      correctKeycode: "-",
      correctKeychar: "-",

      // Speed Debug
      currentDriverSpeed: "-",

      // Build Version
      versionLabel: "-",

      // Keyboard
      lastKeyCode: "-",
      lastKey: "-",
      lastConvertedKey: "-",
    };
  }

  /**
   * Creates and displays the debug overlay
   */
  show() {
    if (this.overlayLeft || this.overlayRight) {
      return; // Already showing
    }

    // Create left overlay container (Login, Mission Config, Trial)
    const overlayLeft = document.createElement("div");
    overlayLeft.id = "debug-overlay-left";
    overlayLeft.style.position = "fixed";
    // overlayLeft.style.top = "10px";
    overlayLeft.style.top = "32vh";
    // overlayLeft.style.left = "10px";
    overlayLeft.style.width = "220px";
    overlayLeft.style.maxHeight = "90vh";
    overlayLeft.style.backgroundColor = "rgba(0, 0, 0, 0.6)";
    overlayLeft.style.color = "#ddd";
    overlayLeft.style.fontFamily = "monospace";
    overlayLeft.style.fontSize = "10px";
    overlayLeft.style.padding = "8px";
    overlayLeft.style.borderRadius = "4px";
    overlayLeft.style.zIndex = "99999";
    overlayLeft.style.border = "1px solid #555";
    overlayLeft.style.overflowY = "auto";

    overlayLeft.innerHTML = `
      <div style="color: #999; font-weight: bold; margin-bottom: 3px;">Login</div>
      <div style="font-size: 9px;">
        <div>Study: <span id="debug-studyid" style="color: #fff;">-</span></div>
        <div>Participant: <span id="debug-participantid" style="color: #fff;">-</span></div>
        <div>Session: <span id="debug-sessionid" style="color: #fff; word-break: break-all; display: block; font-size: 8px;">-</span></div>
      </div>
      
      <div style="color: #999; font-weight: bold; margin-top: 4px; margin-bottom: 3px;">Mission Config</div>
      <div style="font-size: 9px;">
        <div>Mission: <span id="debug-mission-num" style="color: #fff;">-</span></div>
        <div>Source: <span id="debug-metadata-source" style="color: #fff;">-</span></div>
        <div>Layout: <span id="debug-keyboard-layout" style="color: #fff;">-</span></div>
        <div>Build: <span id="debug-build-datetime" style="color: #fff; font-size: 8px;">-</span></div>
        <div>Retries: <span id="debug-max-retries" style="color: #fff;">-</span></div>
        <div>Lives: <span id="debug-lives" style="color: #fff;">-</span></div>
        <div>Max Crash: <span id="debug-max-crash" style="color: #fff;">-</span></div>
        <div>Min Score: <span id="debug-min-score" style="color: #fff;">-</span></div>
        <div>Trials: <span id="debug-num-trials" style="color: #fff;">-</span></div>
        <div>File: <span id="debug-file" style="color: #fff; word-break: break-all; display: block; font-size: 8px;">-</span></div>
        <div>RLexperiment: <span id="debug-rl-experiment" style="color: #fff;">-</span></div>
      </div>
      
      <div style="color: #999; font-weight: bold; margin-top: 4px; margin-bottom: 3px;">Trial</div>
      <div style="font-size: 9px;">
        <div>Trial #: <span id="debug-trial" style="color: #fff;">-</span></div>
        <div>Type: <span id="debug-trial-type" style="color: #fff;">-</span></div>
        ${shouldShowCorrectLane
        ? `<div style="font-size: 8px;">
          Correct Lane: <span id="debug-correct-keycode" style="color: #fff;">-</span>
          (<span id="debug-correct-keychar" style="color: #fff;">-</span>)
        </div>`
        : ""
      }
        <div>Speed: <span id="debug-current-driver-speed" style="color: #fff;">-</span></div>
      </div>
    `;

    // Create right overlay container (Last Key, Key History)
    const overlayRight = document.createElement("div");
    overlayRight.id = "debug-overlay-right";
    overlayRight.style.position = "fixed";
    // overlayLeft.style.top = "10px";
    overlayRight.style.top = "32vh";
    overlayRight.style.right = "0px";
    overlayRight.style.width = "220px";
    overlayRight.style.maxHeight = "90vh";
    overlayRight.style.backgroundColor = "rgba(0, 0, 0, 0.6)";
    overlayRight.style.color = "#ddd";
    overlayRight.style.fontFamily = "monospace";
    overlayRight.style.fontSize = "10px";
    overlayRight.style.padding = "8px";
    overlayRight.style.borderRadius = "4px";
    overlayRight.style.zIndex = "99999";
    overlayRight.style.border = "1px solid #555";
    overlayRight.style.overflowY = "auto";

    overlayRight.innerHTML = `
      <div style="color: #999; font-weight: bold; margin-bottom: 3px;">Last Key</div>
      <div style="font-size: 9px;">
        <div>Code: <span id="debug-keycode" style="color: #fff;">-</span></div>
        <div>Cvt: <span id="debug-converted" style="color: #fff;">-</span></div>
          <div>Raw: <span id="debug-key" style="color: #fff;">-</span></div>
      </div>
      
      <div style="color: #999; font-weight: bold; margin-top: 4px; margin-bottom: 3px;">History (last 5)</div>
      <div id="debug-key-history" style="font-size: 8px;">
        <div style="color: #888;">-</div>
      </div>
    `;

    // Create bottom-left version label container
    const versionBadge = document.createElement("div");
    versionBadge.id = "debug-version-badge";
    versionBadge.style.position = "fixed";
    versionBadge.style.left = "8px";
    versionBadge.style.bottom = "8px";
    versionBadge.style.backgroundColor = "rgba(0, 0, 0, 0.72)";
    versionBadge.style.color = "#fff";
    versionBadge.style.fontFamily = "monospace";
    versionBadge.style.fontSize = "10px";
    versionBadge.style.padding = "4px 8px";
    versionBadge.style.borderRadius = "4px";
    versionBadge.style.zIndex = "99999";
    versionBadge.style.border = "1px solid #666";
    versionBadge.style.letterSpacing = "0.2px";
    versionBadge.style.opacity = "0.95";
    versionBadge.innerHTML = '<span id="debug-version-label">Version: -</span>';

    document.body.appendChild(overlayLeft);
    document.body.appendChild(overlayRight);
    document.body.appendChild(versionBadge);
    this.overlayLeft = overlayLeft;
    this.overlayRight = overlayRight;
    this.versionBadge = versionBadge;

    // Update initial values
    this.render();
  }

  /**
   * Hides and removes the debug overlay
   */
  hide() {
    if (this.overlayLeft && this.overlayLeft.parentNode) {
      this.overlayLeft.parentNode.removeChild(this.overlayLeft);
      this.overlayLeft = null;
    }
    if (this.overlayRight && this.overlayRight.parentNode) {
      this.overlayRight.parentNode.removeChild(this.overlayRight);
      this.overlayRight = null;
    }
    if (this.versionBadge && this.versionBadge.parentNode) {
      this.versionBadge.parentNode.removeChild(this.versionBadge);
      this.versionBadge = null;
    }
  }

  /**
   * Updates the bottom-left version label
   * @param {string} versionLabel - Visible version string (e.g., GF04 | 3.0.0)
   */
  setVersionLabel(versionLabel) {
    this.state.versionLabel = versionLabel || "-";
    this.render();
  }

  /**
   * Updates the login information
   * @param {string} studyID - Study ID
   * @param {string} participantID - Participant ID
   * @param {string} sessionID - Session ID
   */
  setLoginInfo(studyID, participantID, sessionID) {
    this.state.studyID = studyID || "-";
    this.state.participantID = participantID || "-";
    this.state.sessionID = sessionID || "-";
    this.render();
  }

  /**
   * Updates the keyboard layout in the debug overlay
   * @param {string} layoutId - Layout ID (e.g., "QWERTY_USUK", "AZERTY_FR")
   */
  setKeyboardLayout(layoutId) {
    this.state.keyboardLayout = layoutId || "-";
    this.render();
  }

  /**
   * Updates the metadata source label in the debug overlay
   * @param {string} metadataSource - Metadata source label (localmetadata/firebase)
   */
  setMetadataSource(metadataSource) {
    this.state.metadataSource = metadataSource || "-";
    this.render();
  }

  /**
   * Updates the mission configuration information
   * @param {Object} config - Mission config object
   * @param {number} config.mission - Mission number
   * @param {string} config.build_date_time - Build date/time
   * @param {number} config.instructionsMaxRetries - Max retries
   * @param {number} config.missionLives - Mission lives
   * @param {number} config.missionMaxCrash - Max crashes
   * @param {number} config.missionMinScore - Min score
   * @param {number} config.missionNumTrials - Number of trials
   * @param {string} config.thisFile - File URL
   * @param {boolean} config.missionIsAdaptive - Whether this mission requests more trials from the backend once its static trials run out (aka "RLexperiment")
   */
  setMissionConfig(config = {}) {
    this.state.mission = config.mission !== undefined ? config.mission : "-";
    this.state.buildDateTime = config.build_date_time || "-";
    this.state.instructionsMaxRetries = config.instructionsMaxRetries !== undefined ? config.instructionsMaxRetries : "-";
    this.state.missionLives = config.missionLives !== undefined ? config.missionLives : "-";
    this.state.missionMaxCrash = config.missionMaxCrash !== undefined ? config.missionMaxCrash : "-";
    this.state.missionMinScore = config.missionMinScore !== undefined ? config.missionMinScore : "-";
    this.state.missionNumTrials = config.missionNumTrials !== undefined ? config.missionNumTrials : "-";
    this.state.thisFile = config.thisFile || "-";
    this.state.rlExperiment = String(!!config.missionIsAdaptive);
    this.render();
  }

  /**
   * Updates the trial information
   * @param {number|string} trialNumber - Current trial number
   * @param {string} trialType - Type of trial (TASK, INSTRUCTION, etc.)
   * @param {string} correctKeycode - Correct keycode for this trial (optional)
   * @param {string} correctKeychar - Correct key character for this trial (optional)
   */
  setTrial(trialNumber, trialType = null, correctKeycode = null, correctKeychar = null) {
    this.state.trialNumber = trialNumber;
    if (trialType !== null) {
      this.state.trialType = trialType;
    }
    if (correctKeycode !== null) {
      this.state.correctKeycode = correctKeycode;
    }
    if (correctKeychar !== null) {
      this.state.correctKeychar = correctKeychar;
    }
    this.render();
  }

  /**
   * Updates the current speed information
   * @param {Object} config - Speed config object
   * @param {number} config.currentDriverSpeed - Current driver speed
   */
  setSpeedState(config = {}) {
    this.state.currentDriverSpeed =
      config.currentDriverSpeed !== undefined
        ? config.currentDriverSpeed
        : this.state.currentDriverSpeed;
    this.render();
  }

  /**
   * Reads the current driver speed last published via setSpeedState
   * @returns {number|string} The last known driver speed, or "-" if none yet
   */
  getSpeedState() {
    return this.state.currentDriverSpeed;
  }



  /**
   * Records a key press and updates the display
   * @param {string} keyCode - The event.code value (e.g., "KeyS", "Slash")
    * @param {string} key - The raw event.key value (e.g., "s", "/", ";")
   */
  recordKeyPress(keyCode, key) {
    const convertedKey = KEY_CODE_TO_KEY_CONVERSION[keyCode] || "not mapped";

    this.state.lastKeyCode = keyCode;
    this.state.lastKey = key;
    this.state.lastConvertedKey = convertedKey;

    // Add to history
    this.keyHistory.unshift({
      keyCode,
      key,
      converted: convertedKey,
    });

    // Keep only last 5
    if (this.keyHistory.length > this.maxKeyHistory) {
      this.keyHistory = this.keyHistory.slice(0, this.maxKeyHistory);
    }

    this.render();
  }

  /**
   * Renders/updates the overlay content
   */
  render() {
    if (!this.overlayLeft && !this.overlayRight && !this.versionBadge) return;

    // Update login info
    const studyIdEl = document.getElementById("debug-studyid");
    const participantIdEl = document.getElementById("debug-participantid");
    const sessionIdEl = document.getElementById("debug-sessionid");

    if (studyIdEl) studyIdEl.textContent = this.state.studyID;
    if (participantIdEl) participantIdEl.textContent = this.state.participantID;
    if (sessionIdEl) sessionIdEl.textContent = this.state.sessionID;

    // Update mission config
    const missionNumEl = document.getElementById("debug-mission-num");
    const metadataSourceEl = document.getElementById("debug-metadata-source");
    const buildDateTimeEl = document.getElementById("debug-build-datetime");
    const maxRetriesEl = document.getElementById("debug-max-retries");
    const livesEl = document.getElementById("debug-lives");
    const maxCrashEl = document.getElementById("debug-max-crash");
    const minScoreEl = document.getElementById("debug-min-score");
    const numTrialsEl = document.getElementById("debug-num-trials");
    const fileEl = document.getElementById("debug-file");
    const keyboardLayoutEl = document.getElementById("debug-keyboard-layout");
    const rlExperimentEl = document.getElementById("debug-rl-experiment");

    if (missionNumEl) missionNumEl.textContent = this.state.mission;
    if (metadataSourceEl) metadataSourceEl.textContent = this.state.metadataSource;
    if (keyboardLayoutEl) keyboardLayoutEl.textContent = this.state.keyboardLayout;
    if (buildDateTimeEl) buildDateTimeEl.textContent = this.state.buildDateTime;
    if (maxRetriesEl) maxRetriesEl.textContent = this.state.instructionsMaxRetries;
    if (livesEl) livesEl.textContent = this.state.missionLives;
    if (maxCrashEl) maxCrashEl.textContent = this.state.missionMaxCrash;
    if (minScoreEl) minScoreEl.textContent = this.state.missionMinScore;
    if (numTrialsEl) numTrialsEl.textContent = this.state.missionNumTrials;
    if (fileEl) fileEl.textContent = this.state.thisFile;
    if (rlExperimentEl) rlExperimentEl.textContent = this.state.rlExperiment;

    // Update trial info
    const trialEl = document.getElementById("debug-trial");
    const trialTypeEl = document.getElementById("debug-trial-type");
    const correctKeycodeEl = document.getElementById("debug-correct-keycode");
    const correctKeycharEl = document.getElementById("debug-correct-keychar");
    const currentDriverSpeedEl = document.getElementById("debug-current-driver-speed");

    if (trialEl) trialEl.textContent = this.state.trialNumber;
    if (trialTypeEl) trialTypeEl.textContent = this.state.trialType;
    if (shouldShowCorrectLane && correctKeycodeEl) {
      correctKeycodeEl.textContent = this.state.correctKeycode;
    }
    if (shouldShowCorrectLane && correctKeycharEl) {
      correctKeycharEl.textContent = this.state.correctKeychar;
    }
    if (currentDriverSpeedEl) currentDriverSpeedEl.textContent = this.state.currentDriverSpeed;

    // Update keyboard info
    const keyCodeEl = document.getElementById("debug-keycode");
    const keyEl = document.getElementById("debug-key");
    const convertedEl = document.getElementById("debug-converted");
    const keyHistoryEl = document.getElementById("debug-key-history");

    if (keyCodeEl) keyCodeEl.textContent = this.state.lastKeyCode;
    if (keyEl) keyEl.textContent = this.state.lastKey;
    if (convertedEl) convertedEl.textContent = this.state.lastConvertedKey;

    // Update version badge
    const versionLabelEl = document.getElementById("debug-version-label");
    if (versionLabelEl) versionLabelEl.textContent = `Version: ${this.state.versionLabel}`;


    // Update key history
    if (keyHistoryEl) {
      if (this.keyHistory.length === 0) {
        keyHistoryEl.innerHTML = '<div style="color: #888;">-</div>';
      } else {
        keyHistoryEl.innerHTML = this.keyHistory
          .map((entry, index) => {
            return `
              <div style="margin-bottom: 2px; padding: 1px; color: #ddd;">
                <span style="color: #888;">${index + 1}.</span>
                <span style="color: #fff;">${entry.keyCode}</span>
                <span style="color: #888;">→</span>
                <span style="color: #fff;">${entry.converted}</span>
                <span style="color: #888;">→</span>
                <span style="color: #fff;">${entry.key}</span>
              </div>
            `;
          })
          .join("");
      }
    }
  }

  /**
   * Toggles the visibility of the debug overlay
   */
  toggle() {
    if (this.overlayLeft && this.overlayLeft.parentNode) {
      this.hide();
    } else {
      this.show();
    }
  }

  /**
   * Registers the global hotkey (Ctrl + Alt + D) to toggle the debug overlay
   */
  registerToggleHotkey() {
    if (this.hotkeyRegistered) {
      return; // Already registered
    }

    const handleKeyPress = (event) => {
      // Check if typing in an input field
      const isTyping =
        document.activeElement.tagName === "INPUT" ||
        document.activeElement.tagName === "TEXTAREA" ||
        document.activeElement.isContentEditable;

      if (isTyping) return;

      // Check for Ctrl + Alt + D
      if (event.ctrlKey && event.altKey && event.code === "KeyD") {
        event.preventDefault();
        this.toggle();
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    this.hotkeyRegistered = true;
    console.log("Debug overlay hotkey registered: Ctrl + Alt + D");
  }

  /**
   * Clears all state and resets to defaults
   */
  reset() {
    this.state = {
      studyID: "-",
      participantID: "-",
      sessionID: "-",
      mission: "-",
      buildDateTime: "-",
      keyboardLayout: "-",
      instructionsMaxRetries: "-",
      missionLives: "-",
      missionMaxCrash: "-",
      missionMinScore: "-",
      missionNumTrials: "-",
      thisFile: "-",
      metadataSource: "-",
      rlExperiment: "-",
      trialNumber: "-",
      trialType: "-",
      correctKeycode: "-",
      correctKeychar: "-",
      currentDriverSpeed: "-",
      versionLabel: "-",
      lastKeyCode: "-",
      lastKey: "-",
      lastConvertedKey: "-",
    };
    this.keyHistory = [];
    this.render();
  }
}

// Global instance
let globalDebugOverlay = null;

/**
 * Gets or creates the global debug overlay instance
 * @returns {DebugOverlay}
 */
export function getDebugOverlay() {
  if (!globalDebugOverlay) {
    globalDebugOverlay = new DebugOverlay();
  }
  return globalDebugOverlay;
}

/**
 * Shows the debug overlay (creates if needed)
 */
export function showDebugOverlay() {
  const overlay = getDebugOverlay();
  overlay.show();
}

/**
 * Hides the debug overlay
 */
export function hideDebugOverlay() {
  if (globalDebugOverlay) {
    globalDebugOverlay.hide();
  }
}
